// Pure voice-mode logic (task 7.3, Req 11.4/11.6/11.7).
//
// The voice screen (../screens/voice.js) is unavoidably browser-bound: it opens a
// WebSocket, captures the microphone and plays audio back. Everything that can be
// pure lives here instead — the client/server frame shapes, the live-transcript
// reducer, and the PCM/base64 audio codecs — so the tricky bits are unit-tested
// (voice.test.js, node --test) with no DOM, socket or Web Audio dependency.
//
// Wire format (mirrors api/lib/voiceSession.js):
//   client → server: { type:'start' } | { type:'audio', audio:<base64> }
//                     | { type:'text', text } | { type:'stop' } | { type:'ping' }
//   server → client: { type:'transcript', role:'user'|'assistant', text }
//                     | { type:'audio', audio:<base64> } | { type:'session', … }
//                     | { type:'turnComplete' } | { type:'interrupted' } | { type:'pong' }
//                     | { type:'error', message }
// Audio is raw little-endian 16-bit PCM: 16 kHz captured up to the browser, 24 kHz
// played back (Nova Sonic).

// --- Client frame builders -------------------------------------------------

export function startFrame() {
  return { type: 'start' };
}

export function audioFrame(base64) {
  return { type: 'audio', audio: base64 };
}

export function textFrame(text) {
  return { type: 'text', text: String(text == null ? '' : text).trim() };
}

export function stopFrame() {
  return { type: 'stop' };
}

export function pingFrame() {
  return { type: 'ping' };
}

// Serialise a frame for WebSocket.send(). Kept separate so callers never hand-roll
// JSON and tests can assert the exact shape.
export function serializeFrame(frame) {
  return JSON.stringify(frame);
}

// --- Server event parsing --------------------------------------------------

// Known server event types the transcript UI understands. Anything else parses
// through unchanged (so a forward-compatible event won't crash the client) but the
// screen only acts on these.
export const SERVER_EVENT_TYPES = Object.freeze([
  'transcript',
  'audio',
  'session',
  'turnComplete',
  'interrupted',
  'pong',
  'error',
]);

// Parse one inbound WebSocket payload into an event object. Never throws: a
// malformed frame becomes an { type:'error' } event so the UI can surface it
// rather than tearing the session down on a bad byte.
export function parseServerEvent(raw) {
  if (raw == null || typeof raw !== 'string') {
    return { type: 'error', message: 'empty voice event' };
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { type: 'error', message: 'voice event was not JSON' };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || typeof parsed.type !== 'string') {
    return { type: 'error', message: 'voice event missing a type' };
  }
  return parsed;
}

// True when a `session` event tells the client that voice is unavailable and it
// should fall back to typed input (Req 11.6). The server sends this both as an
// explicit signal and when the agent reports voice is down.
export function isVoiceUnavailable(event) {
  return !!event && event.type === 'session' && event.voiceAvailable === false;
}

// --- Live-transcript reducer (Req 11.4) ------------------------------------
//
// Nova Sonic streams a turn as a run of same-role transcript fragments, so we
// coalesce consecutive fragments from one role into a single readable line and
// start a new line when the speaker changes. A `turnComplete` closes the open line
// so the next fragment — even from the same role — begins fresh. State is a plain
// value ({ entries, openRole }); every function returns a new state and never
// mutates its input, which keeps it trivially testable and safe to snapshot.

export function initTranscript() {
  return { entries: [], openRole: null };
}

function normaliseRole(role) {
  return role === 'user' ? 'user' : 'assistant';
}

// Append a transcript fragment. Merges into the currently open line when it is the
// same role, otherwise opens a new line. Blank fragments are ignored.
export function applyTranscript(state, role, text) {
  const base = state || initTranscript();
  const fragment = String(text == null ? '' : text).trim();
  if (fragment === '') return base;
  const r = normaliseRole(role);
  const entries = base.entries.slice();

  if (base.openRole === r && entries.length > 0) {
    const last = entries[entries.length - 1];
    entries[entries.length - 1] = { role: r, text: `${last.text} ${fragment}`.trim() };
  } else {
    entries.push({ role: r, text: fragment });
  }
  return { entries, openRole: r };
}

// Close the open line: the next fragment starts a new entry even if same-role.
export function completeTurn(state) {
  const base = state || initTranscript();
  if (base.openRole === null) return base;
  return { entries: base.entries.slice(), openRole: null };
}

// --- Audio codecs ----------------------------------------------------------
//
// Cross-environment and dependency-free (no atob/btoa, no Buffer) so the same code
// runs in the browser and under node --test. Audio is little-endian signed 16-bit
// PCM, matching what the backend/Nova Sonic expects and returns.

const B64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_LOOKUP = (() => {
  const table = new Int16Array(128).fill(-1);
  for (let i = 0; i < B64_CHARS.length; i += 1) table[B64_CHARS.charCodeAt(i)] = i;
  return table;
})();

export function bytesToBase64(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let out = '';
  for (let i = 0; i < u8.length; i += 3) {
    const b0 = u8[i];
    const has1 = i + 1 < u8.length;
    const has2 = i + 2 < u8.length;
    const b1 = has1 ? u8[i + 1] : 0;
    const b2 = has2 ? u8[i + 2] : 0;
    out += B64_CHARS[b0 >> 2];
    out += B64_CHARS[((b0 & 0x03) << 4) | (b1 >> 4)];
    out += has1 ? B64_CHARS[((b1 & 0x0f) << 2) | (b2 >> 6)] : '=';
    out += has2 ? B64_CHARS[b2 & 0x3f] : '=';
  }
  return out;
}

export function base64ToBytes(str) {
  const s = String(str == null ? '' : str);
  const out = new Uint8Array((s.length >> 2) * 3 + 3);
  let p = 0;
  let buffer = 0;
  let bits = 0;
  for (let i = 0; i < s.length; i += 1) {
    const code = s.charCodeAt(i);
    const v = code < 128 ? B64_LOOKUP[code] : -1;
    if (v < 0) continue; // skip '=' padding, whitespace, newlines
    buffer = (buffer << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[p] = (buffer >> bits) & 0xff;
      p += 1;
    }
  }
  return out.subarray(0, p);
}

// Clamp a Float32 sample stream ([-1, 1]) to signed 16-bit PCM.
export function floatTo16BitPCM(float32) {
  const out = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i += 1) {
    let s = float32[i];
    if (s > 1) s = 1;
    else if (s < -1) s = -1;
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

// Int16 PCM back to Float32 ([-1, 1]) for playback via Web Audio.
export function int16ToFloat32(int16) {
  const out = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i += 1) {
    const s = int16[i];
    out[i] = s < 0 ? s / 0x8000 : s / 0x7fff;
  }
  return out;
}

export function int16ToBytes(int16) {
  const out = new Uint8Array(int16.length * 2);
  for (let i = 0; i < int16.length; i += 1) {
    const s = int16[i];
    out[i * 2] = s & 0xff; // little-endian
    out[i * 2 + 1] = (s >> 8) & 0xff;
  }
  return out;
}

export function bytesToInt16(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const count = u8.length >> 1;
  const out = new Int16Array(count);
  for (let i = 0; i < count; i += 1) {
    const lo = u8[i * 2];
    const hi = u8[i * 2 + 1];
    out[i] = ((hi << 8) | lo) << 16 >> 16; // reassemble signed 16-bit
  }
  return out;
}

// Linear-average downsample from inputRate to targetRate. Used to bring the mic's
// native rate (often 44.1/48 kHz) down to the 16 kHz the backend expects. When the
// rates already match (or target is higher) the input is returned unchanged.
export function downsampleFloat32(input, inputRate, targetRate) {
  if (!(inputRate > 0) || !(targetRate > 0) || targetRate >= inputRate) {
    return input instanceof Float32Array ? input : Float32Array.from(input || []);
  }
  const ratio = inputRate / targetRate;
  const outLength = Math.floor(input.length / ratio);
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i += 1) {
    const start = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), input.length);
    let sum = 0;
    let n = 0;
    for (let j = start; j < end; j += 1) {
      sum += input[j];
      n += 1;
    }
    out[i] = n > 0 ? sum / n : 0;
  }
  return out;
}

// Full mic-capture encode: downsample a Float32 frame to targetRate, quantise to
// 16-bit PCM and base64 it for an { type:'audio' } frame.
export function encodeCaptureFrame(float32, inputRate, targetRate = 16000) {
  const down = downsampleFloat32(float32, inputRate, targetRate);
  const pcm = floatTo16BitPCM(down);
  return bytesToBase64(int16ToBytes(pcm));
}

// Decode a server { type:'audio' } payload to a Float32 sample stream for Web
// Audio playback (at the playback sample rate, typically 24 kHz).
export function decodePlaybackFrame(base64) {
  return int16ToFloat32(bytesToInt16(base64ToBytes(base64)));
}
